import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { trackBudgetRequest, trackServiceView, trackWhatsappClick } from "@/lib/analytics";
import BreadcrumbTrail from "@/components/BreadcrumbTrail";
import { DEFAULT_PROJECTS } from "@/lib/portfolio-data";
import { getServicePageByPath } from "@/lib/service-pages";

const ServicePage = () => {
  const location = useLocation();
  const page = getServicePageByPath(location.pathname);

  useEffect(() => {
    if (page) trackServiceView(page.path);
  }, [page]);

  if (!page) return <Navigate to="/solucoes" replace />;

  return (
    <main id="main-content" className="bg-[#0a0a0a] pt-24">
      <section className="relative overflow-hidden bg-[#ff6b35] noise py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <BreadcrumbTrail items={[{ label: "Soluções", href: "/solucoes" }, { label: page.h1 }]} />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-8 lg:gap-12 items-start">
            <div>
              <div className="inline-block bg-[#0a0a0a] text-white px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-5">
                {page.eyebrow}
              </div>
              <h1 className="font-display text-[#0a0a0a] text-[2.2rem] min-[380px]:text-[2.35rem] sm:text-7xl lg:text-8xl uppercase leading-[0.9]">
                {page.h1}
              </h1>
              <p className="mt-6 max-w-3xl text-[#0a0a0a]/80 text-lg sm:text-xl leading-snug">
                {page.intro}
              </p>
              <a
                href={page.cta.href}
                onClick={() => {
                  trackBudgetRequest(`service_${page.path.replace(/\W+/g, "_")}`);
                  trackWhatsappClick(`service_${page.path.replace(/\W+/g, "_")}`);
                }}
                target="_blank"
                rel="noreferrer"
                className="btn-brutal mt-8 inline-flex"
              >
                <span>{page.cta.label}</span>
                <ArrowRight size={18} className="shrink-0" />
              </a>
              {page.secondaryCta && (
                <Link
                  to={page.secondaryCta.href}
                  className="ml-0 mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#0a0a0a] hover:text-white sm:ml-5 sm:mt-8"
                >
                  <span>{page.secondaryCta.label}</span>
                  <ArrowRight size={16} className="shrink-0" />
                </Link>
              )}
            </div>

            <aside className="brutal-card bg-white p-6 sm:p-7">
              <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
                Benefícios
              </p>
              <ul className="space-y-3">
                {page.benefits.map((benefit) => (
                  <li key={benefit} className="flex gap-3 text-[#0a0a0a]/80 leading-snug">
                    <span className="mt-2 h-2 w-2 bg-[#ff6b35] brutal-border shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-white py-18 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <article className="brutal-card bg-[#0a0a0a] text-white p-6 sm:p-8">
            <h2 className="font-display text-[#ff6b35] text-4xl uppercase leading-none">
              {page.problemHeading || "Problema"}
            </h2>
            <p className="mt-5 text-white/75 leading-snug">{page.problem}</p>
            {page.problemPoints && (
              <ul className="mt-6 grid grid-cols-1 gap-3">
                {page.problemPoints.map((point) => (
                  <li key={point} className="flex gap-3 text-white/75 leading-snug">
                    <span className="mt-2 h-2 w-2 bg-[#ff6b35] brutal-border shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
          <article className="brutal-card bg-[#ff6b35] p-6 sm:p-8">
            <h2 className="font-display text-[#0a0a0a] text-4xl uppercase leading-none">
              Solução BezierClick
            </h2>
            <p className="mt-5 text-[#0a0a0a]/75 leading-snug">{page.solution}</p>
          </article>
        </div>
      </section>

      {page.valueComparison && (
        <section className="bg-white py-18 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none mb-8">
              {page.valueComparison.heading}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {page.valueComparison.rows.map((row) => (
                <article key={row.conventional} className="grid grid-cols-1 sm:grid-cols-2 brutal-border bg-[#f8f5ee]">
                  <div className="border-b-4 border-[#0a0a0a] p-4 sm:border-b-0 sm:border-r-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#ff6b35] mb-2">
                      Website convencional
                    </p>
                    <p className="text-[#0a0a0a]/75 leading-snug">{row.conventional}</p>
                  </div>
                  <div className="bg-[#ff6b35] p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white mb-2">
                      Website BezierClick
                    </p>
                    <p className="font-semibold text-[#0a0a0a] leading-snug">{row.bezierclick}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {page.websiteTypes && (
        <section className="bg-[#0a0a0a] py-18 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            <h2 className="font-display text-white text-4xl sm:text-6xl uppercase leading-none mb-8">
              Tipos de website
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {page.websiteTypes.map((type) => (
                <article key={type.title} className="brutal-card bg-white p-6 min-h-[190px]">
                  <h3 className="font-display text-[#0a0a0a] text-3xl uppercase leading-none">
                    {type.title}
                  </h3>
                  <p className="mt-4 text-[#0a0a0a]/75 leading-snug">{type.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#0a0a0a] py-18 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <h2 className="font-display text-white text-4xl sm:text-6xl uppercase leading-none mb-8">
            Como funciona
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {page.howItWorks.map((step, index) => (
              <article key={step} className="brutal-card bg-white p-6 min-h-[190px]">
                <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-4">
                  0{index + 1}
                </p>
                <p className="text-[#0a0a0a]/80 leading-snug">{step}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {page.relatedProjectPaths && (
        <section className="bg-white py-18 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
              <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none">
                Projetos relacionados
              </h2>
              <Link to="/projetos" className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a] hover:text-[#ff6b35]">
                Ver projetos
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {page.relatedProjectPaths.map((projectPath) => {
                const project = DEFAULT_PROJECTS.find((item) => item.casePath === projectPath);
                if (!project) return null;

                return (
                  <Link key={project.casePath} to={project.casePath} className="brutal-card bg-[#ff6b35] p-6 block">
                    <p className="font-mono text-xs uppercase tracking-widest text-white mb-3">
                      {project.cat}
                    </p>
                    <h3 className="font-display text-[#0a0a0a] text-4xl uppercase leading-none">
                      {project.title}
                    </h3>
                    <p className="mt-4 text-[#0a0a0a]/75 leading-snug">{project.problem}</p>
                    <p className="mt-6 font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                      Ver projeto
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#ff6b35] noise py-18 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none">
              Funcionalidades
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {page.features.map((feature) => (
                <li key={feature} className="bg-white brutal-border px-4 py-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none">
              FAQ
            </h2>
            <div className="mt-8 space-y-4">
              {page.faq.map((item) => (
                <article key={item.question} className="brutal-card bg-white p-5">
                  <h3 className="font-display text-[#0a0a0a] text-2xl uppercase leading-none">
                    {item.question}
                  </h3>
                  <p className="mt-3 text-[#0a0a0a]/75 leading-snug">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] py-18 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
              Próximo passo
            </p>
            <h2 className="font-display text-white text-4xl sm:text-6xl uppercase leading-none">
              Quer transformar o website numa ferramenta comercial?
            </h2>
          </div>
          <a
            href={page.cta.href}
            onClick={() => {
              trackBudgetRequest(`service_final_${page.path.replace(/\W+/g, "_")}`);
              trackWhatsappClick(`service_final_${page.path.replace(/\W+/g, "_")}`);
            }}
            target="_blank"
            rel="noreferrer"
            className="btn-brutal btn-brutal-orange"
          >
            <span>{page.cta.label}</span>
            <ArrowRight size={18} className="shrink-0" />
          </a>
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
